/**
 * pageCapture.js
 * Captures a screenshot of the currently displayed page along with rich DOM/viewport state
 * for comprehensive feedback inspection.
 */
import html2canvas from 'html2canvas';

/**
 * Resizes a canvas if it exceeds max dimensions to ensure lightweight base64 payloads (~50KB-180KB).
 */
function downscaleCanvas(sourceCanvas, maxDimension = 1440) {
  const width = sourceCanvas.width;
  const height = sourceCanvas.height;

  if (width <= maxDimension && height <= maxDimension) {
    return sourceCanvas;
  }

  const ratio = Math.min(maxDimension / width, maxDimension / height);
  const targetWidth = Math.round(width * ratio);
  const targetHeight = Math.round(height * ratio);

  const targetCanvas = document.createElement('canvas');
  targetCanvas.width = targetWidth;
  targetCanvas.height = targetHeight;

  const ctx = targetCanvas.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);
    return targetCanvas;
  }
  return sourceCanvas;
}

export async function capturePageSnapshot(options = {}) {
  const pageContext = {
    url: window.location.href,
    pathname: window.location.pathname + window.location.search + window.location.hash,
    title: document.title || '',
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    screen: {
      width: window.screen?.width || window.innerWidth,
      height: window.screen?.height || window.innerHeight,
      colorDepth: window.screen?.colorDepth
    },
    scroll: {
      x: window.scrollX || window.pageXOffset || 0,
      y: window.scrollY || window.pageYOffset || 0
    },
    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    devicePixelRatio: window.devicePixelRatio || 1,
    online: navigator.onLine,
    language: navigator.language,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };

  let screenshot = null;
  let captureError = null;

  // Temporarily hide elements that shouldn't appear in the background page screenshot (e.g. the feedback modal itself)
  const elementsToHide = document.querySelectorAll('.feedback-banner-modal, [data-feedback-modal="true"]');
  const originalStyles = [];

  elementsToHide.forEach(el => {
    originalStyles.push({ el, visibility: el.style.visibility, opacity: el.style.opacity });
    el.style.visibility = 'hidden';
  });

  try {
    const targetElement = document.getElementById('root') || document.body;

    const canvas = await html2canvas(targetElement, {
      useCORS: true,
      allowTaint: true,
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      ignoreElements: (element) => {
        return (
          element.classList?.contains('feedback-banner-modal') ||
          element.getAttribute('data-feedback-ignore') === 'true' ||
          element.getAttribute('data-feedback-modal') === 'true'
        );
      }
    });

    const optimizedCanvas = downscaleCanvas(canvas, 1440);
    screenshot = optimizedCanvas.toDataURL('image/jpeg', 0.75);
  } catch (err) {
    console.warn('[PageCapture] Screenshot generation notice:', err.message);
    captureError = err.message;
  } finally {
    // Restore hidden elements
    elementsToHide.forEach((el, index) => {
      const original = originalStyles[index];
      if (original) {
        el.style.visibility = original.visibility;
        el.style.opacity = original.opacity;
      }
    });
  }

  return {
    screenshot,
    pageContext,
    captureError
  };
}
